import React from 'react'
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import SubHeader from '../components/common/SubHeader';
import ActionButtons from '../components/workflow/ActionButtons';
import HistoryListModal from '../components/workflow/HistoryListModal';
import { URL_BARCODE, URL_WORKFLOW } from '../constance/urls';
import useWorkflowDetail from '../hooks/useWorkflowDetail';
import { PATH } from '../routes/Routes';
import { Toast } from '../utils/Toast';
import { Media } from "../AppMedia";
import { useIdleTimer } from 'react-idle-timer';
import TimeoutModal from '../components/common/TimeoutModal';
import { defaultWoodImportWorkflowDetail } from '../constance/dataTypes/workflow';
import WoodImportDetail from '../components/wood-import/WoodImportDetail';
import ReportExportButton from '../components/common/ReportExportButton';
import { HistoryOutlined, FileTextFilled } from '@ant-design/icons';
import { Spin, Button, Layout, Space, Popover, Divider, Alert } from 'antd';
import Title from 'antd/es/typography/Title';
import { Content } from 'antd/es/layout/layout';
import { BLUE_COLOR, Green_COLOR } from '../constance/color';
import ColorButton from '../components/common/ColorButton';

function WoodImportDetailView() {

  const params = useParams();
  const history = useHistory();
  const { t } = useTranslation();

  const {
    response,
    fetchData,
    handleAction,
    isLoading,
    errorMessages,
  } = useWorkflowDetail({
    workflowUrl: URL_WORKFLOW.WOOD_IMPORT_WORKFLOW,
    id: params.id,
    onSuccess: (action) => {
      history.push(PATH.WOOD_IMPORT);
      Toast.success(`${t(`workflows.actions.${action}`)} ${t('success')}`)
    },
    manual: params.id === 'add'
  });

  const { getRemainingTime, getLastActiveTime } = useIdleTimer({
    timeout: 1000 * 60 * 30, // 30 minutes
    onIdle: () => setIsTimeout(true),
    crossTab: true
  })

  const [isTimeout, setIsTimeout] = React.useState(false);
  const [detail, setDetail] = React.useState(defaultWoodImportWorkflowDetail);
  const [isActionLoading, setIsActionLoading] = React.useState(false);
  const [isDetailLoading, setIsDetailLoading] = React.useState(false);
  const [isPrinting, setIsPrinting] = React.useState(false);
  const detailRef = React.useRef(null);
  const [isPopoverVisible, setIsPopoverVisible] = React.useState(false);


  // history modal
  const [isHistoryModalVisible, setIsHistoryModalVisible] = React.useState(false);

  React.useEffect(() => {
    setDetail({ ...defaultWoodImportWorkflowDetail, ...response })
  }, [response])

  React.useEffect(() => {
    if (params.id != 'add') {
      fetchData();
    }
  }, [params.id])

  const handleSaveWorkflowDetail = async () => {
    setIsActionLoading(true);
    if (!detailRef.current) {
      return false;
    }
    const [isCreate, isSuccess, returnData] = await detailRef.current.handleSaveDetail();
    if (isSuccess) {
      Toast.success(`${t('saving')} ${t('success')}`);
      if (isCreate) {
        history.replace(`${PATH.WOOD_IMPORT}${returnData.workflow}`)
      } else {
        fetchData();
      }
    } else {
      Toast.error(`${t('saving')} ${t('error')} ${t('view_debug')}`);
    }
    setIsActionLoading(false);
    return isSuccess;
  }

  const handleDoAction = async (action) => {
    if (detail.workflow_params.formEditable) {
      const isSuccess = await handleSaveWorkflowDetail();
      if (!isSuccess) {
        return;
      }
    }
    handleAction(action)
  }

  return (
    <div>


      <SubHeader />
      <Media lessThan="md">
        <TimeoutModal
          open={isTimeout}
          onClose={() => history.push(PATH.HOME)}
        />
      </Media>
      <Layout style={{ alignItems: 'center', textAlign: 'left', margin: '24px', }}>

        <Spin spinning={isLoading} fullscreen size='large' />

        <Content style={{ width: '95%' }} >
          {errorMessages &&
            <Alert message={t('error')} description={errorMessages} type="error" showIcon />
          }
          {detail.memo &&
            <Alert message={t('memo')} description={detail.memo} type="warning" showIcon />
          }
          <Media greaterThan="sm">
            <div >
              <Title level={2} style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1em' }}>
                {t('wood_imports.detail')}{detail.state_name ? ` - [${detail.state_name}]` : ''}
                {params.id != 'add' &&
                  <div>
                    <Space>

                      <Popover
                        placement="bottom"
                        arrow
                        open={isPopoverVisible}
                        onOpenChange={(visible) => { setIsPopoverVisible(visible) }}
                        content={
                          (['pdf', 'excel'].map(type => (
                            <ReportExportButton
                              url={`${URL_BARCODE.REPORT_WOOD_IMPORT_EXPORT}${type}/?id=${detail.wood_import.id}`}
                              type={type}
                              title={`${t('wood_imports.detail')}.....`}
                              disabled={isPrinting}
                              onStarted={() => {
                                setIsPrinting(true)
                                setIsPopoverVisible(false)
                              }}
                              onFinished={() => setIsPrinting(false)}
                            />
                          )
                          ))
                        }
                      >
                        <ColorButton
                          icon={<FileTextFilled />}
                          color='primary'
                          variant="outlined"
                          override={Green_COLOR}
                        >
                          {t('wood_imports.print')}
                        </ColorButton>
                      </Popover>

                      <ColorButton
                        icon={<HistoryOutlined />}
                        variant="outlined"
                        color='primary'
                        override={BLUE_COLOR}
                        onClick={() => setIsHistoryModalVisible(true)}
                      >{t('workflows.history')}</ColorButton>

                    </Space>
                  </div>
                }
              </Title>
            </div>
            <Divider />
          </Media>

          <Media at="sm">
            <Title level={4} style={{ marginTop: '1em' }}>
              {t('wood_imports.detail')}{detail.state_name ? ` - [${detail.state_name}]` : ''}
            </Title>
            {params.id != 'add' &&
              <ColorButton
                icon={<HistoryOutlined />}
                variant="outlined"
                color="primary"
                override={BLUE_COLOR}
                onClick={() => setIsHistoryModalVisible(true)}
              >{t('workflows.history')}</ColorButton>
            }
          </Media>

          <WoodImportDetail
            ref={detailRef}
            detail={detail.wood_import}
            enableEdit={detail.workflow_params.formEditable}
            setDetailLoading={(value) => setIsDetailLoading(value)}
          />
          <br />
          <ActionButtons
            enableSave={detail.workflow_params.formEditable}
            enableCreate={params.id === 'add'}
            handleCreate={() => handleSaveWorkflowDetail()}
            handleSave={() => handleSaveWorkflowDetail()}
            actions={detail.workflow_actions}
            handleAction={(action) => handleDoAction(action)}
            isLoading={isLoading || isActionLoading || isDetailLoading}
          />

          <HistoryListModal
            open={isHistoryModalVisible}
            onClose={() => setIsHistoryModalVisible(false)}
            workflowHistoryUrl={`${URL_WORKFLOW.WOOD_IMPORT_WORKFLOW_HISTORY}${params.id}`}
          />

        </Content>
      </Layout>
    </div>
  )
}

export default WoodImportDetailView;

